import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { useToasts } from '../Toasts';
import API from '../../Services/API';
import { ulid } from 'ulid';
import { usePreference } from '../../Data/Preference';
import { useSocketListener, useSocketRecipient } from '../Socket';
import Actions from './Actions';
import { useRedirect } from '../../Services/helpers';
import { useLocation } from 'react-router-dom';
import { useSession } from '../Session';
import { useMobile } from '../Mobile';

const AssistantContext = createContext();

export const useAssistantContext = () => {
    return useContext(AssistantContext);
};

const recognitionAvailable = !!(window.webkitSpeechRecognition || window.SpeechRecognition);
let recognition;
if (recognitionAvailable) {
    recognition = window.webkitSpeechRecognition ?
        new window.webkitSpeechRecognition() :
        new window.SpeechRecognition();
    recognition.lang = "en-GB";
    recognition.interimResults = true;
}

const styleFloating = 'floating';
const styleModal = 'modal';

const socketTopic = 'assistant';

let actions;

export const Provider = ({ children }) => {
    const {isReady} = useSession();
    const [assistantOpen, setAssistantOpen] = usePreference('assistant:open', false);
    const [mobileStyle, setMobileStyle] = usePreference('assistant:mobileStyle', styleFloating);
    const [conversationId, setConversationId] = useState(null);
    const [conversation, setConversation] = useState(null);
    const [entries, setEntries] = useState([]);
    const [dictating, setDictating] = useState(false);
    const [tmpQuestion, setTmpQuestion] = useState('');
    const [sendingQuestion, setSendingQuestion] = useState(null);
    const [assistantTyping, setAssistantTyping] = useState(false);
    const [streamingAnswers, setStreamingAnswers] = useState(['']);
    const [activeToolTitles, setActiveToolTitles] = useState(null);
    const [unseen, setUnseen] = useState(false);
    const {showError} = useToasts();
    const {Redirect, go} = useRedirect();
    const currentPath = useLocation()?.pathname;
    const isMobile = useMobile();
    const dictationAvailable = recognitionAvailable && !isMobile;

    const loading = useRef(false);
    const markingSeen = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    if (!actions) {
        actions = new Actions(go);
    }

    const unlessLoading = (fn) => {
        if (!isReady) {
            return;
        }
        if (loading.current) {
            return;
        }

        loading.current = true;
        setIsLoading(true);

        return fn().finally(() => {
            loading.current = false
            setIsLoading(false);
        });
    }

    // We need a second listener for admin messages because they don't come through the assistant topic
    useSocketListener(
        {entity: {entityModel: 'Assistant/Conversation', entityId: conversationId}},
        (msg) => {
            const ignore = [
                'ConversationWasReviewed',
                'ConversationWasMarkedAsSeen',
                'ConversationWasFinished',
                'ConversationWasArchived',
            ];
            if (ignore.includes(msg.event)) {
                return;
            }

            // If we get an ID which doesn't match, it's probably an archived conversation
            if (msg.conversation && msg.conversation.id !== conversationId) {
                return
            }

            if (msg.eventData && msg.eventData?.role === 'Admin') {
                setEntries(existing => {
                    if (existing.some(entry => entry.message === msg.eventData.message && entry.role === 'Admin')) {
                        return existing;
                    }
                    return [...existing, {role: 'Admin', message: msg.eventData.message}];
                });
            }
            if (msg.conversation) {
                saveConversation(msg.conversation, true);
            }
        }
    );

    // These are only used for streaming answers
    useSocketListener({topic: socketTopic}, (msg) => {

        if (msg.event === 'ConversationWasStarted' && msg.conversation) {
            saveConversation(msg.conversation, false);
            return;
        }

        if (msg.assistantTyping) {
            setAssistantTyping(true);
        }
        if (msg.chunk) {
            setStreamingAnswers(existing => {
                const last = existing[existing.length - 1];
                const updated = last.endsWith(msg.chunk) ? last : last + msg.chunk;
                return [...existing.slice(0, -1), updated];
            });
            setAssistantTyping(false);
            setActiveToolTitles([]);
        }
        if (msg.toolTitle) {
            setActiveToolTitles(existing => {
                if (!existing) {
                    return [msg.toolTitle];
                }
                if (existing.includes(msg.toolTitle)) {
                    return existing;
                }
                return [...existing, msg.toolTitle];
            });
            setStreamingAnswers(existing => [...existing, '']);
            setAssistantTyping(false);
        }
    });
    const socketRecipient = useSocketRecipient(socketTopic);

    useEffect(() => {
        if (!dictationAvailable) {
            return;
        }

        const onResult = (event) => setTmpQuestion(event.results[0][0].transcript);
        const onSpeechEnd = () => stopDictating();

        const onError = (event) => {
            console.error(event);
            setDictating(false);
            recognition.stop();
            showError(`Speech recognition error detected: ${event.error}`)
        }

        recognition.addEventListener('result', onResult);
        recognition.addEventListener('speechend', onSpeechEnd);
        recognition.addEventListener('error', onError);

        return () => {
            recognition.removeEventListener('result', onResult);
            recognition.removeEventListener('speechend', onSpeechEnd);
            recognition.removeEventListener('error', onError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loading.current) {
            return;
        }

        if (!assistantOpen && conversationId) {
            return;
        }

        if (!conversationId) {
            restoreConversation();
            return;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, assistantOpen, conversationId, conversation]);

    useEffect(() => {
        if (assistantOpen && unseen > 0 && !markingSeen.current) {
            markAsSeen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unseen, assistantOpen]);

    useEffect(() => {
        if (assistantOpen && conversationId) {
            refreshConversation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantOpen]);

    const newConversation = (humanAttention = false, trigger = 'UserStarted') => {
        setConversationId(null);
        setConversation(null);
        setSendingQuestion(null);
        setEntries([]);
        setAssistantTyping(true);
        startConversation(humanAttention, trigger);
    }

    const startConversation = (humanAttention = false, trigger = 'UserStarted') => {
        unlessLoading(() => {
            const conversationId = ulid();
            setConversationId(conversationId);
            setAssistantTyping(true);
            return API.post(`/assistant/${conversationId}`, {socketRecipient, currentPath, trigger, humanAttention}).then(conversation => {
                saveConversation(conversation, false);
            }).catch(e => {
                setConversationId(null);
                showError('Failed to start conversation with assistant');
                console.error(e);
            });
        });
    }

    const requestHumanAttention = () => {
        unlessLoading(() => {
            return API.post(`/assistant/${conversationId}/humanAttention`).then(conversation => {
                refreshConversation();
            });
        });
    }

    const restoreConversation = () => {
        if (conversationId) {
            return;
        }

        unlessLoading(() => {
            return API.get(`/assistant/started`).then(conversation => {
                saveConversation(conversation, false);
            }).catch(e => {
                setConversationId(null);
                setConversation(null);
                setEntries([]);
                if (assistantOpen) {
                    // We need to wait until unlessLoading has finished before starting the conversation
                    // because it sets isLoading to true
                    setTimeout(() => startConversation(), 100);
                    setTimeout(() => startConversation(), 500);
                }
            });
        });
    }

    const refreshConversation = () => {
        unlessLoading(() => {
            return API.get(`/assistant/${conversationId}`).then(conversation => {
                saveConversation(conversation, true);
            });
        });
    }

    const submitMessage = (message) => {
        unlessLoading(() => {
            setTmpQuestion('');
            setSendingQuestion(message);
            return API.post(`/assistant/${conversationId}/messages`, {message, socketRecipient, currentPath}).then(conversation => {
                saveConversation(conversation, true);
            }).catch(e => {
                showError('Failed to send message to assistant');
                clearStreaming();
                setTmpQuestion(message.trim());
                console.error(e);
            });
        });
    }

    const markAsSeen = () => {
        if (markingSeen.current) {
            return;
        }
        markingSeen.current = true;
        setUnseen(0);
        API.post(`/assistant/${conversationId}/seen`).catch(e => {
            console.error(e);
        }).finally(() => {
            setUnseen(0);
            markingSeen.current = false;
        });
    }

    const saveConversation = (conversation, performActions) => {
        actions.handleActions(conversation, performActions);

        if (conversation.status === 'Archived') {
            setConversationId(null);
            setConversation(null);
            setEntries([]);
            setUnseen(0);
            return;
        }

        setConversationId(conversation.id);
        setConversation(conversation);
        setUnseen(conversation.unseenByUser ?? 0);

        if (conversation.entries) {
            setEntries(conversation.entries);
            clearStreaming();
        }
    }

    const clearStreaming = () => {
        setStreamingAnswers(['']);
        setSendingQuestion(null);
        setActiveToolTitles([]);
        setAssistantTyping(false);
    }

    const startDictating = () => {
        setDictating(true);
        recognition.start();
    }

    const stopDictating = () => {
        recognition.stop();
        setDictating(false);
        if (tmpQuestion?.trim()) {
            submitMessage(tmpQuestion.trim());
        }
    }

    const toggleMobileStyle = () => setMobileStyle(mobileStyle === styleFloating ? styleModal : styleFloating);

    const toggleDictation = () => dictating ? stopDictating() : startDictating();


    const providerValues = {
        assistantOpen: isReady && assistantOpen,
        mobileStyle,
        toggleMobileStyle,
        conversation,
        entries,
        recognitionAvailable,
        dictationAvailable,
        dictating,
        setAssistantOpen,
        toggleDictation,
        startDictating,
        stopDictating,
        tmpQuestion,
        setTmpQuestion,
        sendingQuestion,
        submitMessage,
        newConversation,
        requestHumanAttention,
        assistantTyping,
        streamingAnswers,
        activeToolTitles,
        isLoading,
        unseen,
    };

    return (
        <AssistantContext.Provider value={providerValues}>
            {children}
            <Redirect />
        </AssistantContext.Provider>
    );
};
